import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { TCPADisclosure } from '../components/TCPADisclosure';
import { LegalLinks } from '../components/LegalLinks';
import { BookOpenIcon, CheckCircle2Icon, ChevronDownIcon, ChevronLeftIcon, LoaderIcon, GraduationCapIcon, UsersIcon, ClockIcon, DollarSignIcon, ShieldCheckIcon, HeartIcon, BrainIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
const hcuEducationLevels = [{
  label: 'High School Diploma',
  value: '1'
}, {
  label: 'No High School Diploma',
  value: '2'
}, {
  label: 'GED',
  value: '3'
}, {
  label: '0-12 College Credits',
  value: '4'
}, {
  label: 'More Than 12 College Credits',
  value: '5'
}, {
  label: 'Associates Degree',
  value: '6'
}, {
  label: 'Bachelors Degree',
  value: '7'
}];
const hcuStartTimeframes = [{
  label: 'Immediately',
  value: '1'
}, {
  label: '1-3 Months',
  value: '2'
}, {
  label: '4-6 Months',
  value: '3'
}, {
  label: '7-12 Months',
  value: '4'
}, {
  label: 'Later Than 1 Year',
  value: '5'
}];
function getGradYearOptions(): string[] {
  const years: string[] = [];
  for (let y = 2026; y >= 1980; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
const degreeLevels = [{
  label: "Bachelor's",
  value: 'bachelors'
}, {
  label: "Master's",
  value: 'masters'
}, {
  label: 'Doctoral',
  value: 'doctoral'
}];
interface HcuProgram {
  shortTitle: string;
  longTitle: string;
  degreeLevel: string;
  areaOfStudy: string;
  mProgram: string;
}
const hcuPrograms: HcuProgram[] = [
// Bachelor's - Arts
{
  shortTitle: 'B.A. - Biblical Studies',
  longTitle: 'Bachelor of Arts (B.A.) in Biblical Studies - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67002'
}, {
  shortTitle: 'B.A. - Criminal Justice',
  longTitle: 'Bachelor of Arts (B.A.) in Criminal Justice - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67003'
}, {
  shortTitle: 'B.A. - Interdisciplinary Studies - Business Track',
  longTitle: 'Bachelor of Arts (B.A.) in Interdisciplinary Studies - Business Track - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67004'
}, {
  shortTitle: 'B.A. - Interdisciplinary Studies - General Math and Science Track',
  longTitle: 'Bachelor of Arts (B.A.) in Interdisciplinary Studies - General Math and Science Track - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67034'
}, {
  shortTitle: 'B.A. - Interdisciplinary Studies - Humanities Track',
  longTitle: 'Bachelor of Arts (B.A.) in Interdisciplinary Studies - Humanities Track - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67033'
}, {
  shortTitle: 'B.A. - Interdisciplinary Studies',
  longTitle: 'Bachelor of Arts (B.A.) in Interdisciplinary Studies - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67051'
}, {
  shortTitle: 'B.A. - Psychology - Christian Counseling',
  longTitle: 'Bachelor of Arts (B.A.) in Psychology - Christian Counseling - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67006'
}, {
  shortTitle: 'B.A. - Psychology - Marriage and Family Studies',
  longTitle: 'Bachelor of Arts (B.A.) in Psychology - Marriage and Family Studies - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67007'
}, {
  shortTitle: 'B.A. - Psychology',
  longTitle: 'Bachelor of Arts (B.A.) in Psychology - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67008'
}, {
  shortTitle: 'B.A. - Psychology to Master of Arts in Psychology (5 Year Accelerated)',
  longTitle: 'Bachelor of Arts (B.A.) in Psychology to Master of Arts in Psychology (5 Year Accelerated) - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67048'
}, {
  shortTitle: 'B.A. - Theological Studies',
  longTitle: 'Bachelor of Arts (B.A.) in Theological Studies - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'arts',
  mProgram: '67009'
},
// Bachelor's - Business Administration
{
  shortTitle: 'B.B.A. - Business Administration',
  longTitle: 'Bachelor of Business Administration (B.B.A.) in Business Administration - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '67005'
}, {
  shortTitle: 'B.B.A. - Business Data Analytics',
  longTitle: 'Bachelor of Business Administration (B.B.A.) in Business Data Analytics - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '67052'
}, {
  shortTitle: 'B.B.A. - Human Resource Development and Change',
  longTitle: 'Bachelor of Business Administration (B.B.A.) in Human Resource Development and Change - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '67053'
}, {
  shortTitle: 'B.B.A. - International Business',
  longTitle: 'Bachelor of Business Administration (B.B.A.) in International Business - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '67031'
}, {
  shortTitle: 'B.B.A. - Management Information Systems',
  longTitle: 'Bachelor of Business Administration (B.B.A.) in Management Information Systems - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '67054'
}, {
  shortTitle: 'B.B.A. - Management',
  longTitle: 'Bachelor of Business Administration (B.B.A.) in Management - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '67001'
}, {
  shortTitle: 'B.B.A. - Marketing',
  longTitle: 'Bachelor of Business Administration (B.B.A.) in Marketing - Online',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '67032'
},
// Master's - Arts
{
  shortTitle: 'M.A. - Apologetics',
  longTitle: 'Master of Arts (M.A.) in Apologetics - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67011'
}, {
  shortTitle: 'M.A. - Christian Leadership - Practical Theology Track',
  longTitle: 'Master of Arts (M.A.) in Christian Leadership - Practical Theology Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67014'
}, {
  shortTitle: 'M.A. - Clinical Mental Health Counseling',
  longTitle: 'Master of Arts (M.A.) in Clinical Mental Health Counseling - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67015'
}, {
  shortTitle: 'M.A. - Human Services - Marriage and Family Track',
  longTitle: 'Master of Arts (M.A.) in Human Services - Marriage and Family Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67016'
}, {
  shortTitle: 'M.A. - Human Services - Professional Life Coaching Track',
  longTitle: 'Master of Arts (M.A.) in Human Services - Professional Life Coaching Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67040'
}, {
  shortTitle: 'M.A. - Marriage and Family Therapy',
  longTitle: 'Master of Arts (M.A.) in Marriage and Family Therapy - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67018'
}, {
  shortTitle: 'M.A. - Psychology - Applied Project Track',
  longTitle: 'Master of Arts (M.A.) in Psychology - Applied Project Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67041'
}, {
  shortTitle: 'M.A. - Psychology - Teaching Track',
  longTitle: 'Master of Arts (M.A.) in Psychology - Teaching Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67044'
}, {
  shortTitle: 'M.A. - Psychology - Thesis Track',
  longTitle: 'Master of Arts (M.A.) in Psychology - Thesis Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67042'
}, {
  shortTitle: 'M.A. - Theological Studies - Theological Studies Track',
  longTitle: 'Master of Arts (M.A.) in Theological Studies - Theological Studies Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'arts',
  mProgram: '67019'
},
// Master's - Business Administration
{
  shortTitle: 'M.B.A. - Professional MBA in Accounting and Finance',
  longTitle: 'Master of Business Administration (M.B.A.) in Professional MBA in Accounting and Finance - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '67039'
}, {
  shortTitle: 'M.B.A. - Professional MBA in Business Data Analytics',
  longTitle: 'Master of Business Administration (M.B.A.) in Professional MBA in Business Data Analytics - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '67013'
}, {
  shortTitle: 'M.B.A. - Professional MBA in Management',
  longTitle: 'Master of Business Administration (M.B.A.) in Professional MBA in Management - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'business',
  mProgram: '67010'
},
// Master's - Divinity
{
  shortTitle: 'M.Div. - Biblical Languages Track',
  longTitle: 'Master of Divinity (M.Div.) in Biblical Languages Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'divinity',
  mProgram: '67020'
}, {
  shortTitle: 'M.Div. - English Language Track',
  longTitle: 'Master of Divinity (M.Div.) in English Language Track - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'divinity',
  mProgram: '67021'
},
// Master's - Education
{
  shortTitle: 'M.Ed. - Curriculum and Instruction',
  longTitle: 'Master of Education (M.Ed.) in Curriculum and Instruction - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '67022'
}, {
  shortTitle: 'M.Ed. - Educational Administration',
  longTitle: 'Master of Education (M.Ed.) in Educational Administration - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '67023'
}, {
  shortTitle: 'M.Ed. - Professional School Counseling',
  longTitle: 'Master of Education (M.Ed.) in Professional School Counseling - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'education',
  mProgram: '67045'
},
// Master's - Health Administration
{
  shortTitle: 'M.H.A. - Professional Science Masters in Health Professions',
  longTitle: 'Master of Health Administration (M.H.A.) in Professional Science Masters in Health Professions - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'health',
  mProgram: '67017'
},
// Master's - Science
{
  shortTitle: 'M.S. - Artificial Intelligence',
  longTitle: 'Master of Science (M.S.) in Artificial Intelligence - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67028'
}, {
  shortTitle: 'M.S.C.J. - Criminal Justice',
  longTitle: 'Master of Science (M.S.C.J.) in Criminal Justice - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67012'
}, {
  shortTitle: 'M.S. - Computer and Information Sciences',
  longTitle: 'Master of Science (M.S.) in Computer and Information Sciences - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67036'
}, {
  shortTitle: 'M.S. - Cybersecurity',
  longTitle: 'Master of Science (M.S.) in Cybersecurity - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67043'
}, {
  shortTitle: 'M.S. - Data Science',
  longTitle: 'Master of Science (M.S.) in Data Science - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67037'
}, {
  shortTitle: 'M.S. - Human Resource Management in People Analytics',
  longTitle: 'Master of Science (M.S.) in Human Resource Management in People Analytics - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67026'
}, {
  shortTitle: 'M.S. - Kinesiology - Sports Management',
  longTitle: 'Master of Science (M.S.) in Kinesiology - Sports Management - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67027'
}, {
  shortTitle: 'M.S. - Leadership',
  longTitle: 'Master of Science (M.S.) in Leadership - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67035'
}, {
  shortTitle: 'M.S. - Learning, Technology, and Design',
  longTitle: 'Master of Science (M.S.) in Learning, Technology, and Design - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'science',
  mProgram: '67025'
},
// Master's - Nursing
{
  shortTitle: 'M.S.N. - Family Nurse Practitioner',
  longTitle: 'Master of Science in Nursing (M.S.N.) in Family Nurse Practitioner - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'nursing',
  mProgram: '67038'
}, {
  shortTitle: 'M.S.N. - Nurse Education',
  longTitle: 'Master of Science in Nursing (M.S.N.) in Nurse Education - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'nursing',
  mProgram: '67046'
}, {
  shortTitle: 'M.S.N. - Nurse Leadership',
  longTitle: 'Master of Science in Nursing (M.S.N.) in Nurse Leadership - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'nursing',
  mProgram: '67047'
}, {
  shortTitle: 'M.S.N. - Pediatric Nurse Practitioner',
  longTitle: 'Master of Science in Nursing (M.S.N.) in Pediatric Nurse Practitioner - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'nursing',
  mProgram: '67049'
}, {
  shortTitle: 'M.S.N. - RN to MSN',
  longTitle: 'Master of Science in Nursing (M.S.N.) in RN to MSN - Online',
  degreeLevel: 'masters',
  areaOfStudy: 'nursing',
  mProgram: '67050'
},
// Doctoral
{
  shortTitle: 'Ed.D. - Executive Educational Leadership',
  longTitle: 'Doctor of Education (Ed.D.) in Executive Educational Leadership - Online',
  degreeLevel: 'doctoral',
  areaOfStudy: 'education',
  mProgram: '67029'
}, {
  shortTitle: 'Ed.D. - Mental Health and Human Services',
  longTitle: 'Doctor of Education (Ed.D.) in Mental Health and Human Services - Online',
  degreeLevel: 'doctoral',
  areaOfStudy: 'education',
  mProgram: '67030'
}, {
  shortTitle: 'Ed.S. - Educational Leadership',
  longTitle: 'Educational Specialist (Ed.S.) in Educational Leadership - Online',
  degreeLevel: 'doctoral',
  areaOfStudy: 'education',
  mProgram: '67024'
}];
export function HCULandingPage() {
  usePageMeta('Houston Christian University | Online Degree Programs', 'Explore flexible online degree programs at Houston Christian University. Faith-based education combining reason, liberal arts, and technical training for working adults.', {
    ogImage: 'https://cdn.magicpatterns.com/uploads/7rHHFpReLC371VmGSsY1sD/67119a3a74d41da2543935f5_Houston_Christian_University_nav.png',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const formRef = useRef<HTMLDivElement>(null);
  const [selectedDegreeLevel, setSelectedDegreeLevel] = useState('');
  const [selectedAreaOfStudy, setSelectedAreaOfStudy] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [formStep, setFormStep] = useState(0);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [userIP, setUserIP] = useState('');
  const [urlParams] = useState<Record<string, string>>(() => {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  });
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();
  useEffect(() => {
    fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => setUserIP(data.ip || '')).catch(() => setUserIP(''));
  }, []);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const areaLabels: Record<string, string> = {
    arts: 'Arts',
    business: 'Business Administration',
    divinity: 'Divinity',
    education: 'Education',
    health: 'Health Administration',
    science: 'Science',
    nursing: 'Nursing'
  };
  const allAreaSlugs = Object.keys(areaLabels);
  const getAvailableAreas = () => {
    if (!selectedDegreeLevel || selectedDegreeLevel === 'undecided') {
      return allAreaSlugs;
    }
    const areasWithPrograms = new Set<string>();
    hcuPrograms.forEach(prog => {
      if (prog.degreeLevel === selectedDegreeLevel) {
        areasWithPrograms.add(prog.areaOfStudy);
      }
    });
    return allAreaSlugs.filter(slug => areasWithPrograms.has(slug));
  };
  const getAvailablePrograms = () => {
    if (!selectedAreaOfStudy) return [];
    return hcuPrograms.filter(prog => {
      const areaMatch = prog.areaOfStudy === selectedAreaOfStudy;
      const degreeMatch = !selectedDegreeLevel || prog.degreeLevel === selectedDegreeLevel;
      return areaMatch && degreeMatch;
    });
  };
  const handleDegreeLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLevel = e.target.value;
    setSelectedDegreeLevel(newLevel);
    setSelectedAreaOfStudy('');
    setSelectedProgram('');
  };
  const handleAreaOfStudyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newArea = e.target.value;
    setSelectedAreaOfStudy(newArea);
    setSelectedProgram('');
  };
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newProgram = e.target.value;
    setSelectedProgram(newProgram);
    if (newProgram && formStep === 0) {
      setFormStep(1);
    }
  };
  const currentProgram = hcuPrograms.find(p => p.mProgram === selectedProgram);
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    const zipValid = validateUniStep2();
    const addrValid = formData.address.trim().length > 0;
    if (!addrValid) {
      setAddressError('Street address is required.');
    } else {
      setAddressError('');
    }
    if (zipValid && addrValid) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = async () => {
    if (validateUniStep3()) {
      setIsSubmitting(true);
      try {
        const params = new URLSearchParams({
          ...urlParams,
          mProgram: currentProgram?.mProgram || '',
          programTitle: currentProgram?.longTitle || '',
          degreeLevel: selectedDegreeLevel,
          areaOfStudy: selectedAreaOfStudy,
          highestEducation: formData.currentEducation,
          gradYear: formData.gradYear,
          startTimeframe: formData.startDate,
          zipCode: formData.zipCode,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: stripPhone(formData.phone),
          leadIdToken: leadIdToken,
          trustedFormUrl: trustedFormUrl,
          webLandingURL: window.location.href,
          userIP: userIP
        });
        await fetch(`https://hooks.zapier.com/hooks/catch/6973915/4496hy3/?${params.toString()}`, {
          method: 'GET',
          mode: 'no-cors'
        });
      } catch (err) {
        console.error('Webhook submission error:', err);
      } finally {
        setIsSubmitting(false);
      }
      navigate('/thank-you', {
        state: {
          formData,
          partner: {
            name: 'Houston Christian University',
            logo: 'https://cdn.magicpatterns.com/uploads/7rHHFpReLC371VmGSsY1sD/67119a3a74d41da2543935f5_Houston_Christian_University_nav.png',
            primaryColor: '#002F6C',
            accentColor: '#A6192E',
            backUrl: '/universities',
            programTitle: currentProgram?.longTitle || ''
          }
        }
      });
    }
  };
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const totalSteps = 3;
  const progressPercent = formStep === 0 ? 0 : Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#002F6C] focus:border-[#002F6C]'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#002F6C] focus:border-[#002F6C]'}`;
  const availableAreas = getAvailableAreas();
  const availablePrograms = getAvailablePrograms();
  const renderFormContent = () => {
    if (formStep === 0) {
      return <div className="space-y-4" data-id="element-2081" data-mp="gmcqv1pvsd4m">
          <div data-id="element-2082" data-mp="bqdyl31fmayx">
            <label htmlFor="degree-level-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2083" data-mp="bqxgwkuzv1dh">
              Degree Level
            </label>
            <div className="relative" data-id="element-2084" data-mp="lm7gl1xx6wo3">
              <select id="degree-level-select" value={selectedDegreeLevel} onChange={handleDegreeLevelChange} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#002F6C] focus:border-[#002F6C] font-medium text-gray-800" data-id="element-2085" data-mp="3u3c3lod2ztzy">
                <option value="" data-id="element-2086" data-mp="29ezxes740de3">-- Select Degree Level --</option>
                {degreeLevels.map(level => <option key={level.value} value={level.value} data-id="element-2087" data-mp="1zndgcf83bk3f">
                    {level.label}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-2088" data-mp="2ndr61z37d6uu">
                <ChevronDownIcon className="h-4 w-4" data-id="element-2089" data-mp="2s9opwfdocvcd" />
              </div>
            </div>
          </div>

          <div data-id="element-2090" data-mp="2idv2u22bxjgc">
            <label htmlFor="area-of-study-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2091" data-mp="2idlf6l4rbodt">
              Area of Study
            </label>
            <div className="relative" data-id="element-2092" data-mp="16zrat1fay5dx">
              <select id="area-of-study-select" value={selectedAreaOfStudy} onChange={handleAreaOfStudyChange} disabled={!selectedDegreeLevel} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#002F6C] focus:border-[#002F6C] font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400" data-id="element-2093" data-mp="16wlkjiuxsxgm">
                <option value="" data-id="element-2094" data-mp="262m7utb7sakk">
                  {selectedDegreeLevel ? '-- Select Area of Study --' : 'Select degree level first'}
                </option>
                {availableAreas.map(slug => <option key={slug} value={slug} data-id="element-2095" data-mp="1s5p6hxxgzapf">
                    {areaLabels[slug]}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-2096" data-mp="kp10skn1p69i">
                <ChevronDownIcon className="h-4 w-4" data-id="element-2097" data-mp="2sdduwdp3xrpt" />
              </div>
            </div>
          </div>

          <div data-id="element-2098" data-mp="2weftcsb79zqq">
            <label htmlFor="program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2099" data-mp="2wdxtllpbx3e2">
              Program
            </label>
            <div className="relative" data-id="element-2100" data-mp="365k93u8p9omm">
              <select id="program-select" value={selectedProgram} onChange={handleProgramChange} disabled={!selectedAreaOfStudy} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#002F6C] focus:border-[#002F6C] font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400" data-id="element-2101" data-mp="366lno8oxct93">
                <option value="" data-id="element-2102" data-mp="1ps3ixcpcph2b">
                  {!selectedAreaOfStudy ? 'Select area of study first' : '-- Select Program --'}
                </option>
                {availablePrograms.map(prog => <option key={prog.mProgram} value={prog.mProgram} data-id="element-2103" data-mp="2t1przghdaj92">
                    {prog.shortTitle}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-2104" data-mp="36yl7nrs4j8y3">
                <ChevronDownIcon className="h-4 w-4" data-id="element-2105" data-mp="3gyvk3a6c4cc7" />
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center pt-2" data-id="element-2106" data-mp="3c0kb11nv65v1">
            Select all three options above to continue
          </p>
        </div>;
    }
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-2107" data-mp="3lpbz2ypt060o">
          <div data-id="element-2108" data-mp="3lqdjhtspm4v2">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2109" data-mp="3lrydnib43lse">
              Selected Program
            </label>
            <div className="relative" data-id="element-2110" data-mp="i6xxdqx7mrbx">
              <select id="step1-program" value={selectedProgram} onChange={e => {
              setSelectedProgram(e.target.value);
            }} className="block w-full px-4 py-3 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-2111" data-mp="i7z81uabdbt4">
                {availablePrograms.map(prog => <option key={prog.mProgram} value={prog.mProgram} data-id="element-2112" data-mp="3vkj1inl5l2rd">
                    {prog.shortTitle}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400" data-id="element-2113" data-mp="3loz9ic9pmo7w">
                <ChevronDownIcon className="h-4 w-4" data-id="element-2114" data-mp="3qn0tczeymrkf" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-2115" data-mp="3cnrlqo4tl5s2" />

          <div data-id="element-2116" data-mp="3gniaynep7duh">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2117" data-mp="3gna0f261skmg">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-2118" data-mp="3s2wtqgk6jot6">
              <option value="" data-id="element-2119" data-mp="3e7b201mkaqx2">-- Please Select --</option>
              {hcuEducationLevels.map(level => <option key={level.value} value={level.value} data-id="element-2120" data-mp="34feuy12hzqgp">
                  {level.label}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-2121" data-mp="2uh7quitb52w4">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-2122" data-mp="2qgp0kt72c4w1">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2123" data-mp="2qg5q1xjqsis1">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-2124" data-mp="1a11at5xw07tn">
              <option value="" data-id="element-2125" data-mp="madq1k2vhnpo">-- Please Select --</option>
              {hcuStartTimeframes.map(opt => <option key={opt.value} value={opt.value} data-id="element-2126" data-mp="r9qm8vfmw74x">
                  {opt.label}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-2127" data-mp="10yrzweypicfq">{errors.startDate}</p>}
          </div>

          <div data-id="element-2128" data-mp="3i334zom60d7">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2129" data-mp="3ibcwipfwpk6">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-2130" data-mp="3ebldtr4mh2y4">
              <option value="" data-id="element-2131" data-mp="39e21iwrhj08t">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-2132" data-mp="2vmoj5m5bc8fd">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-2133" data-mp="30fq348kdmy5t">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-2134" data-mp="2ln649k49fcu6">
            <button type="button" onClick={() => setFormStep(0)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-2135" data-mp="2qp54il4pdtmw">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-2136" data-mp="1y0u3qxl6xvv9" />
              Back
            </button>
            <button type="button" onClick={handleStep1Next} className="flex-1 bg-gradient-to-r from-[#002F6C] to-[#001A3D] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-2137" data-mp="1t3c2zuhbumyc">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-2138" data-mp="2c0vd71fiubmc">
          <div data-id="element-2139" data-mp="2qqb2eczem8zx">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2140" data-mp="2qq17qc3ezdnw">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-2141" data-mp="2zqfxporbpgxq" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-2142" data-mp="267fyszo98d2a">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-2143" data-mp="2b72ctbbwpxc5">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-2144" data-mp="2g2hmdr2vt72r" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-2145" data-mp="2p3xyq4d9bj9m">
                <div data-id="element-2146" data-mp="qpmht8gn4feb">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2147" data-mp="qqfu0oydbx3u">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#002F6C] focus:border-[#002F6C] bg-gray-50" placeholder="City" data-id="element-2148" data-mp="n99wvmkcv714" />
                </div>
                <div data-id="element-2149" data-mp="116q4v2iav8uz">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2150" data-mp="1189lwvbs5nxt">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#002F6C] focus:border-[#002F6C] bg-gray-50" placeholder="State" data-id="element-2151" data-mp="s8mjq7b88e3p" />
                </div>
              </div>
              <div data-id="element-2152" data-mp="vopfx11ho56g">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2153" data-mp="vl1n643vbmss">
                  Street Address
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => {
              updateFormData('address', e.target.value);
              if (addressError) setAddressError('');
            }} className={`block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${addressError ? 'border-red-400' : 'border-gray-300 focus:ring-[#002F6C] focus:border-[#002F6C]'}`} placeholder="123 Main St" data-id="element-2154" data-mp="1sd56n0gc4kl5" />
                {addressError && <p className="mt-1 text-xs text-red-600" data-id="element-2155" data-mp="3rklxz6inbb6v">{addressError}</p>}
              </div>
            </>}

          <div className="flex gap-3 pt-2" data-id="element-2156" data-mp="9deci4dlg7pa">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-2157" data-mp="9ccwrmlmux18">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-2158" data-mp="37ui8sjsk3mmx" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-gradient-to-r from-[#002F6C] to-[#001A3D] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-2159" data-mp="3mnbxmvtpxf49">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-2160" data-mp="y1jw0vt5h47x">
          <div className="grid grid-cols-2 gap-3" data-id="element-2161" data-mp="y0ihuigu81lz">
            <div data-id="element-2162" data-mp="12si9zoqo1q80">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2163" data-mp="12s09gfwz0pua">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-2164" data-mp="a4w8qn4wuxob" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-2165" data-mp="o19ifa37dus8">{errors.firstName}</p>}
            </div>
            <div data-id="element-2166" data-mp="1ziixlkfau0nq">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2167" data-mp="1zhzqyzv3myp0">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-2168" data-mp="24oo3m32hh79b" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-2169" data-mp="1qtvh2ko6eqt8">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-2170" data-mp="1gximzlopyaxy">
            <div data-id="element-2171" data-mp="1gybwguf2bejs">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2172" data-mp="1gzvdin8jltmm">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-2173" data-mp="37arbtb3kop0p" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-2174" data-mp="3gsn08v98k2rt">{errors.email}</p>}
            </div>
            <div data-id="element-2175" data-mp="3lqydzh2nixfb">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-2176" data-mp="3lqetbslm9uxl">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-2177" data-mp="2xuk9aax4nvrd" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-2178" data-mp="1b5pd1rvvains">{errors.phone}</p>}
            </div>
          </div>

          <TCPADisclosure checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} checkboxClassName="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#002F6C] focus:ring-[#002F6C] cursor-pointer flex-shrink-0" data-id="element-2179" data-mp="168725vyahtd5">
            By submitting this form, I confirm that the information provided on
            this form is accurate and complete. I agree that Houston Christian
            University and its affiliates may call and/or text me about its
            offerings using automated technology. I understand consent is not a
            condition of purchasing services.
          </TCPADisclosure>

          <div className="flex gap-3 pt-2" data-id="element-2180" data-mp="8jvu25801411">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-2181" data-mp="4i20egw810ad">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-2182" data-mp="2owxakq7mhfc7" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} disabled={isSubmitting} className="flex-1 bg-gradient-to-r from-[#A6192E] to-[#7A1222] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-2183" data-mp="2kvbqokwo80si">
              {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-2184" data-mp="33n4iwd3x1hz6" />}
              {isSubmitting ? 'Submitting...' : 'Request Info'}
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-2185" data-mp="3co2m4t6j5lgp">
      {/* Header */}
      <header className="bg-white py-2 sm:py-3 px-4 sm:px-12 flex items-center justify-center shadow-sm" data-id="element-2186" data-mp="3f3ods2rh64">
        <img src="https://cdn.magicpatterns.com/uploads/7rHHFpReLC371VmGSsY1sD/67119a3a74d41da2543935f5_Houston_Christian_University_nav.png" alt="Houston Christian University Logo" className="h-10 sm:h-16 w-auto object-contain" data-id="element-2187" data-mp="51gp35cz3skr" />
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#002F6C] to-[#001A3D] relative overflow-hidden" data-id="element-2188" data-mp="3dfp7xlpp14zq">
        {/* Explicit grid placement rather than a duplicated list: on mobile the
            form follows the headline so it lands within reach, and the selling
            points move below it. */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-24 grid gap-8 lg:grid-cols-2 lg:gap-x-16 lg:gap-y-6 relative z-10" data-id="element-2189" data-mp="38gmdswb94j6j">
          {/* Headline */}
          <div className="text-white max-w-xl mx-auto lg:mx-0 text-center lg:text-left lg:col-start-1 lg:row-start-1 lg:self-end" data-id="element-2190" data-mp="2zgpk2ga08u4m">
            <p className="text-white font-medium mb-3 text-lg tracking-wide" data-id="element-2191" data-mp="153vijieivymi">
              Houston Christian University
            </p>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.1] text-white tracking-tight" data-id="element-2192" data-mp="vbhc4cci23u0">
              {currentProgram ? currentProgram.longTitle : <>Online Degrees at Houston Christian University</>}
            </h1>
          </div>

          {/* Right Form */}
          <div ref={formRef} className="w-full lg:w-[520px] lg:justify-self-end bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] p-6 sm:p-10 relative z-10 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-center" data-id="element-2193" data-mp="1izh23a1zlq7j">
            <form onSubmit={e => e.preventDefault()} id="hcu-lead-form" data-id="element-2194" data-mp="3lunl97hpt06">
              {/* Hidden inputs for tracking */}
              <input type="hidden" name="xxTrustedFormCertUrl" id="xxTrustedFormCertUrl" defaultValue="" data-id="element-2195" data-mp="3upt4clj9s7ba" />
              <input type="hidden" name="leadid_token" id="leadid_token" defaultValue="" data-id="element-2196" data-mp="2f8y9ctv913w7" />

              <h2 className="text-2xl sm:text-3xl font-bold text-[#002F6C] mb-4" data-id="element-2197" data-mp="2o102s86whjwz">
                Take the next step in your education.
              </h2>

              {/* Progress Bar */}
              <div className="w-full bg-gray-100 rounded-full h-2 mb-8 overflow-hidden" data-id="element-2198" data-mp="2xy4usonfg9lp">
                <div className="bg-gradient-to-r from-[#A6192E] to-[#7A1222] h-2 rounded-full transition-all duration-500 ease-out" style={{
                width: `${progressPercent}%`
              }} data-id="element-2199" data-mp="32y0mppiuoakw"></div>
              </div>

              <AnimatePresence mode="wait" data-id="element-2200" data-mp="1clbixpoy110e">
                <motion.div key={formStep} initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-2201" data-mp="1cmcx4319a3mc">
                  {renderFormContent()}
                </motion.div>
              </AnimatePresence>
            </form>
          </div>

          {/* Selling points. Placed after the form in the markup so mobile gets
              them below it; the grid puts them back under the headline on
              desktop. */}
          <ul className="space-y-2.5 max-w-lg mx-auto lg:mx-0 lg:col-start-1 lg:row-start-2 lg:self-start" data-id="element-2202" data-mp="3iyknyk2mxyj">
            {['Integrates faith and reason in academics', 'Courses available at any time of the day', '60-year academic tradition focused on quality', 'Some of the lowest tuition available today', 'SACSCOC Accredited'].map(item => <li key={item} className="flex items-start text-base lg:text-lg text-white" data-id="element-2203" data-mp="1razayseit754">
                <CheckCircle2Icon className="w-5 h-5 mt-0.5 text-white/80 mr-3 flex-shrink-0" data-id="element-2204" data-mp="1hijszv384ik8" />
                {item}
              </li>)}
          </ul>
        </div>
      </section>

      {/* Reasons to Choose HCU */}
      <section className="bg-white py-16 border-b border-gray-200" data-id="element-2205" data-mp="2a7gyexmc6kvk">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2206" data-mp="1w90sva07n0f0">
          <div className="mb-10" data-id="element-2207" data-mp="1wakdfcswfxml">
            <div className="w-12 h-1 bg-[#002F6C] mb-4" data-id="element-2208" data-mp="216j3d4tvlnp4"></div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#002F6C]" data-id="element-2209" data-mp="2195cva0bud9z">
              Reasons to Choose HCU
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2210" data-mp="pt6wel2tkoyq">
            <div className="border-r-0 md:border-r border-gray-200 pr-0 md:pr-8" data-id="element-2211" data-mp="2rhbd0x50umci">
              <div className="w-14 h-14 bg-[#002F6C]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-2212" data-mp="2rj5rptpp9i85">
                <HeartIcon className="w-7 h-7 text-[#002F6C]" data-id="element-2213" data-mp="2hri649ftqvel" />
              </div>
              <h3 className="text-lg font-bold text-[#002F6C] mb-2" data-id="element-2214" data-mp="2hpf4es5b7du1">
                Faith-Based Education
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2215" data-mp="28qk05lltsdtd">
                Integrate Christian values with academic excellence in every
                program.
              </p>
            </div>
            <div className="border-r-0 md:border-r border-gray-200 pr-0 md:pr-8" data-id="element-2216" data-mp="1yse1rdz89pt9">
              <div className="w-14 h-14 bg-[#002F6C]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-2217" data-mp="1ur26artoaad5">
                <BrainIcon className="w-7 h-7 text-[#002F6C]" data-id="element-2218" data-mp="3t6ewpnrnpso2" />
              </div>
              <h3 className="text-lg font-bold text-[#002F6C] mb-2" data-id="element-2219" data-mp="3t4c7h5ybw3ke">
                Liberal Arts Core
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2220" data-mp="1axkeqkgoky16">
                Develop critical thinking through faith, reason, and liberal
                arts curriculum.
              </p>
            </div>
            <div data-id="element-2221" data-mp="1kmvgtn55h2fk">
              <div className="w-14 h-14 bg-[#002F6C]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-2222" data-mp="1koezfkea3pki">
                <ClockIcon className="w-7 h-7 text-[#002F6C]" data-id="element-2223" data-mp="1oo7bw86smfv5" />
              </div>
              <h3 className="text-lg font-bold text-[#002F6C] mb-2" data-id="element-2224" data-mp="rzhsgdfipf72">
                Flexible Online Learning
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-2225" data-mp="wxt2ao5tbppa">
                Study on your schedule with fully online degree programs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose HCU */}
      <section className="py-16 bg-gray-50" data-id="element-2226" data-mp="2cet8927fqbpb">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2227" data-mp="2ce1ocon7a2ev">
          <div className="flex flex-col lg:flex-row items-start gap-12" data-id="element-2228" data-mp="2hccw8ttxq2ug">
            <div className="flex-1" data-id="element-2229" data-mp="1t0kmr5rir7c8">
              <div className="w-12 h-1 bg-[#002F6C] mb-4" data-id="element-2230" data-mp="1t1m2vone6k0t"></div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#002F6C] mb-8" data-id="element-2231" data-mp="1o29f91op58x6">
                Why Choose <span className="text-[#002F6C]" data-id="element-2232" data-mp="21sw3u10qtifj">HCU</span>
              </h2>
              <ul className="space-y-4" data-id="element-2233" data-mp="21uz37bnscnwx">
                <li className="flex items-start" data-id="element-2234" data-mp="1x25uydt3yxg3">
                  <CheckCircle2Icon className="w-5 h-5 text-[#002F6C] mr-3 flex-shrink-0 mt-0.5" data-id="element-2235" data-mp="1x0bdxakyjpha" />
                  <span className="text-gray-700" data-id="element-2236" data-mp="2bter75rinh27">
                    <strong data-id="element-2237" data-mp="26r5y89en8f03">Faith-integrated curriculum</strong> combining
                    Christian teachings with academic rigor
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2238" data-mp="2gpvho6m6u7ig">
                  <CheckCircle2Icon className="w-5 h-5 text-[#002F6C] mr-3 flex-shrink-0 mt-0.5" data-id="element-2239" data-mp="2gr6ka33wd97z" />
                  <span className="text-gray-700" data-id="element-2240" data-mp="qe93mbdvauca">
                    <strong data-id="element-2241" data-mp="mcvunsfhab15">Liberal arts foundation</strong> that develops
                    critical thinking and ethical leadership
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2242" data-mp="ga0zv7ooseim">
                  <CheckCircle2Icon className="w-5 h-5 text-[#002F6C] mr-3 flex-shrink-0 mt-0.5" data-id="element-2243" data-mp="g8r2kkpnrad1" />
                  <span className="text-gray-700" data-id="element-2244" data-mp="2hum1s90ajmc">
                    <strong data-id="element-2245" data-mp="7hgyw0kx9xum">Fully online programs</strong> designed for working
                    adults and busy schedules
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2246" data-mp="3oryka4gx2ozy">
                  <CheckCircle2Icon className="w-5 h-5 text-[#002F6C] mr-3 flex-shrink-0 mt-0.5" data-id="element-2247" data-mp="3tp73m8dswz2s" />
                  <span className="text-gray-700" data-id="element-2248" data-mp="3fpxozd58ud0x">
                    <strong data-id="element-2249" data-mp="3fr8tjf6ijot3">Expert faculty</strong> with real-world experience
                    and commitment to student success
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2250" data-mp="1dyc7yyez93uq">
                  <CheckCircle2Icon className="w-5 h-5 text-[#002F6C] mr-3 flex-shrink-0 mt-0.5" data-id="element-2251" data-mp="1iwm1l4l1zwe8" />
                  <span className="text-gray-700" data-id="element-2252" data-mp="2kos8hs3u5q7x">
                    <strong data-id="element-2253" data-mp="2kq3bhppgitxz">Career-focused education</strong> preparing you for
                    leadership in your field and community
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2254" data-mp="2ulxx8toz954z">
                  <CheckCircle2Icon className="w-5 h-5 text-[#002F6C] mr-3 flex-shrink-0 mt-0.5" data-id="element-2255" data-mp="2zdfxtgn393pf" />
                  <span className="text-gray-700" data-id="element-2256" data-mp="33f1k1sliiuca">
                    <strong data-id="element-2257" data-mp="33e89shnchmpe">Supportive Christian community</strong> of students,
                    faculty, and staff
                  </span>
                </li>
              </ul>
            </div>
            <div className="lg:w-[400px] flex-shrink-0" data-id="element-2258" data-mp="3deiltyxn8o2z">
              <img src="https://cdn.magicpatterns.com/uploads/jUSjuibhb7LUe6o3g91xYz/ChatGPT_Image_Jul_1,_2026,_01_59_51_AM.png" alt="Student studying at Houston Christian University" className="w-full h-[400px] object-cover rounded-2xl shadow-lg" data-id="element-2259" data-mp="3ic0nr5d8u31h" />
            </div>
          </div>
        </div>
      </section>

      {/* Accreditation Section */}
      <section className="py-16 bg-white" data-id="element-2260" data-mp="bwpbmmaqc45">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2261" data-mp="av973qfazfk">
          <div className="w-12 h-1 bg-[#002F6C] mx-auto mb-6" data-id="element-2262" data-mp="3shaar0tlgbny"></div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#002F6C] mb-6" data-id="element-2263" data-mp="3nhe7x4wmuq9z">
            Accreditation = Quality Education
          </h2>
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-2264" data-mp="o0pqbxhrza5m">
            Houston Christian University is accredited by the Southern
            Association of Colleges and Schools Commission on Colleges (SACSCOC)
            to award associate, baccalaureate, master's, and doctoral degrees.
          </p>
          <p className="text-gray-600 leading-relaxed" data-id="element-2265" data-mp="a1pyyi2voh5r">
            Our accreditation ensures that you receive a{' '}
            <strong data-id="element-2266" data-mp="2zpgu589mho68">quality education</strong> that is recognized by employers
            and other institutions. Whether you're{' '}
            <strong data-id="element-2267" data-mp="2w9vx0b0p198r">starting your college journey</strong> or{' '}
            <strong data-id="element-2268" data-mp="2w7jdhtfaeqtl">advancing your career</strong>, you can trust that your HCU
            degree represents excellence.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#002F6C] py-16" data-id="element-2269" data-mp="2n8o9mnzptstg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2270" data-mp="2s7izub00ijnl">
          <div className="w-12 h-1 bg-white/40 mx-auto mb-6" data-id="element-2271" data-mp="2dj4ofboj5mvf"></div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2" data-id="element-2272" data-mp="2dhtl1cyzyh4u">
            Ready to Start Your Journey at{' '}
            <span className="text-white" data-id="element-2273" data-mp="2ibphuc3ixxyf">HCU?</span>
          </h2>
          <div className="mt-8" data-id="element-2274" data-mp="24k1464dmn1fc">
            <button onClick={scrollToForm} className="inline-block bg-white text-[#002F6C] font-bold py-3.5 px-12 rounded-full hover:shadow-lg hover:bg-gray-100 transition-all text-sm tracking-wide" data-id="element-2275" data-mp="24gn1sw4yqs7w">
              Get Started Now
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F5F5F5] text-gray-500 py-10 text-xs border-t border-gray-200" data-id="element-2276" data-mp="1fycxypi672ol">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3" data-id="element-2277" data-mp="1azs73got5vct">
          <p data-id="element-2278" data-mp="1pwal19oamtfm">
            Houston Christian University is accredited by the Southern
            Association of Colleges and Schools Commission on Colleges to award
            associate, baccalaureate, master's, and doctoral degrees.
          </p>
          <p data-id="element-2279" data-mp="1yumle3eyp5l9">
            For more information about HCU's online programs, visit{' '}
            <a href="https://hc.edu/online-learning/" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#002F6C]" data-id="element-2280" data-mp="1twtbp0eg9tg8">
              hc.edu/online-learning
            </a>
            .
          </p>
          <div className="pt-4 border-t border-gray-300 space-y-3" data-id="element-2281" data-mp="2hnqg7s3x1ohg">
            <LegalLinks data-id="element-2282" data-mp="2dlvdcj6c731k" />
            <p className="text-gray-400" data-id="element-2283" data-mp="2dmmyt167zke4">
              © {new Date().getFullYear()} Houston Christian University. All
              rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}